import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InputmaskOptions } from '@ngneat/input-mask';
import { Popover } from 'bootstrap';
import { every, isArray, isNil, isObject, pick } from 'lodash';
import {
  catchError,
  concat,
  concatMap,
  defer,
  from,
  map,
  of,
  takeWhile
} from 'rxjs';
import { ClientRegisteredVendorForm } from 'src/app/classes/form-groups/client-registered-vendor-form';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import { LoadingMask } from 'src/app/classes/loading-mask';
import {
  Address,
  ClientRegisteredVendor,
  Contact,
  User,
  Vendor
} from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ConfirmRunBusinessVerificationModalComponent } from 'src/app/modals/confirm-run-business-verification-modal/confirm-run-business-verification-modal.component';
import { NormalizeAddressesModalComponent } from 'src/app/modals/normalize-addresses-modal/normalize-addresses-modal.component';
import { AccountingSystemSelectionService } from 'src/app/services/accounting-system-selection.service';
import { AuthService } from 'src/app/services/auth.service';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import {
  CompanyComplianceTypeDto,
  CompanyDto
} from 'src/app/shared/vive-api/vive-api-models';
import { einSsnValidator } from 'src/app/validators/ein-ssn-validator';
import { vendorNameLengthModalInputs } from 'src/app/validators/vendor-name-length-validator';

@Component({
  selector: 'app-add-edit-client-registered-vendor',
  templateUrl: './add-edit-client-registered-vendor.component.html',
  styleUrls: ['./add-edit-client-registered-vendor.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditClientRegisteredVendorComponent implements OnInit {
  constructor(
    private HostElement: ElementRef,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private AuthService: AuthService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private AccountingSystemSelectionService: AccountingSystemSelectionService,
    private UtilityService: UtilityService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private Router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {
    const navigation = this.Router.currentNavigation();
    this.LoggedInUser = this.AuthService.getUser();
    const { UserAccountType } = this.LoggedInUser;

    // Read navigation state before the add-mode gate / company load so
    // conversion flows are not misclassified as new-vendor creates.
    if (navigation && navigation.extras) {
      const { state } = navigation.extras;

      if (state) {
        const { attemptToConvertToFullProfile, VendorRecord } = state;

        this.attemptToConvertToFullProfile = attemptToConvertToFullProfile;

        if (VendorRecord) {
          this.Vendor = VendorRecord as Vendor;
        }
      }
    }

    if (UserAccountType) {
      const { name } = UserAccountType;

      if (name === 'Client') {
        this.CompanyId = this.AuthService.getUserCompanyId();

        // Mark add-mode before loadCompanyData() so the
        // allowClientRegisteredVendors gate runs even when the company
        // response returns before ngOnInit.
        const ClientRegisteredVendorId = Number(
          this.route.snapshot.paramMap.get('ClientRegisteredVendorId')
        );
        if (
          !this.ClientRegisteredVendor &&
          !this.attemptToConvertToFullProfile &&
          !ClientRegisteredVendorId
        ) {
          this.addingClientRegisteredVendor = true;
        }

        this.loadCompanyData();
      }
    }

    const MailingAddressControl =
      this.ClientRegisteredVendorForm.controls.MailingAddress;
    const StreetAddressControl =
      this.ClientRegisteredVendorForm.controls.StreetAddress;

    MailingAddressControl.valueChanges.subscribe({
      next: (changes) => {
        if (changes && this.isMailingAndStreetSame) {
          StreetAddressControl.patchValue(
            pick(changes, this.requiredAddressFields),
            { emitEvent: false }
          );
          this.ChangeDetectorRef.markForCheck();
        }
      }
    });

    StreetAddressControl.controls.isStreet.setValue(true);
    MailingAddressControl.controls.isMailing.setValue(true);

    this.primaryContactForm.controls.isPrimaryCompliance.setValue(true);
    this.primaryContactForm.controls.isCompliance.setValue(true);
    this.removeStreetAndMailingAddress(this.primaryContactForm);
  }

  @ViewChild('clientRegisteredVendorComponent')
  set clientRegisteredVendorComponentInitialized(
    ClientRegisteredVendorComponent: ElementRef
  ) {
    if (ClientRegisteredVendorComponent) {
      const popoverTriggerList: Element[] =
        ClientRegisteredVendorComponent.nativeElement.querySelectorAll(
          '[name="popover"]'
        );

      Array.from(popoverTriggerList).map((popoverTriggerEl) => {
        return new Popover(popoverTriggerEl, { trigger: 'hover' });
      });
    }
  }

  @Input() ClientRegisteredVendor?: ClientRegisteredVendor;
  @Input() Vendor: Vendor | null = null;
  @Input() isUploadedFromFile: boolean = false;
  @Input() attemptToConvertToFullProfile: boolean = false;

  CompanyId?: number;
  oldClientRegisteredVendor?: ClientRegisteredVendor;
  oldPrimaryContact: Contact | null = null;
  editingClientRegisteredVendor: boolean = false;
  addingClientRegisteredVendor: boolean = false;
  Company!: CompanyDto;
  businessVerifiedOrNonVerified!: boolean;
  isBusinessVerifiedVendor: boolean = false;
  CompanyComplianceTypes: CompanyComplianceTypeDto[] = [];
  CompanyComplianceTypeId?: number;
  taxIdentificationOptions: ('EIN' | 'SSN')[] = ['EIN', 'SSN'];
  YesNoOptions: { label: 'Yes' | 'No'; value: boolean }[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];
  runBusinessVerification: boolean = false;
  sendInvitationEmail: boolean = false;
  isDuplicate: boolean = false;

  // VPDW-763: accounting systems the user designated for this vendor's creation
  // (undefined = no selection made / not applicable → backend keeps prior behavior).
  selectedApConfigIds?: number[];

  ClientRegisteredVendorForm: ClientRegisteredVendorForm =
    new ClientRegisteredVendorForm();
  isMailingAndStreetSame: boolean = false;
  LocalizationInfo!: LocalizationInfo;
  states!: SubNationalDivision[];

  einMask = this.UtilityService.einMask;
  ssnMask = this.UtilityService.ssnMask;
  gstHstMask = this.UtilityService.gstHstMask;

  phoneMask = this.UtilityService.phoneMask;
  zipMask?: InputmaskOptions<any>;

  additionalContacts: ContactForm[] = [];
  primaryContactForm: ContactForm = new ContactForm();
  // Used to help prevent double clicking
  disabled: boolean = false;
  LoggedInUser: User;

  requiredAddressFields = ['address1', 'city', 'state', 'zip'];

  isTakeOverChanged() {
    const {
      value: { isTakeOver }
    } = this.ClientRegisteredVendorForm;
    if (isTakeOver) {
      const {
        controls: { requesterFirstName, requesterLastName }
      } = this.ClientRegisteredVendorForm;
      [requesterFirstName, requesterLastName].forEach((requesterControl) => {
        requesterControl.removeValidators([Validators.required]);
        requesterControl.patchValue(null);
        requesterControl.updateValueAndValidity();
      });
    }

    // Can apply values from the oldClientRegisteredVendor value if isTakeOver is changed to 'No'/false.
  }

  isOffsiteChanged() {
    const {
      value: { isOffsite }
    } = this.ClientRegisteredVendorForm;
    this.primaryContactForm.controls['email'][
      isOffsite ? 'disable' : 'enable'
    ]();
  }

  onIsMailingAndStreetSameChange() {
    this.isMailingAndStreetSame = !this.isMailingAndStreetSame;

    const mailingAddress =
      this.ClientRegisteredVendorForm.controls.MailingAddress;
    const streetAddress =
      this.ClientRegisteredVendorForm.controls.StreetAddress;

    if (this.isMailingAndStreetSame && streetAddress) {
      streetAddress.disable();
      streetAddress.patchValue(
        pick(mailingAddress.value, [
          'address1',
          'address2',
          'city',
          'state',
          'zip'
        ])
      );
    } else if (!this.isMailingAndStreetSame && streetAddress) {
      streetAddress.enable();
      streetAddress.patchValue({
        address1: undefined,
        address2: undefined,
        city: undefined,
        state: undefined,
        zip: undefined
      });
    }
  }

  hasValues(obj: {}): boolean {
    if (!obj || typeof obj !== 'object') return false;

    const hasValidValue = (value: any) => value !== null && value !== false;

    return Object.values(obj).some((value) => {
      if (isObject(value)) {
        return this.hasValues(value); // Recursively check for nested objects
      }
      return hasValidValue(value);
    });
  }

  addContact() {
    const NewContact = new ContactForm();
    this.removeStreetAndMailingAddress(NewContact);

    this.additionalContacts.push(NewContact);
  }

  private setClientRegisteredVendor(): void {
    if (!this.ClientRegisteredVendor) {
      this.ClientRegisteredVendor = new ClientRegisteredVendor({});
    }

    Object.assign(
      this.ClientRegisteredVendor,
      this.ClientRegisteredVendorForm.value
    );

    if (this.addingClientRegisteredVendor) {
      Object.assign(this.ClientRegisteredVendor, {
        country: this.Company.country
      });
    }

    this.ClientRegisteredVendor.CompanyId = this.CompanyId;

    const mailingAddress =
      this.ClientRegisteredVendorForm.controls.MailingAddress;
    const streetAddress =
      this.ClientRegisteredVendorForm.controls.StreetAddress;

    if (mailingAddress && this.hasValues(mailingAddress.value)) {
      this.ClientRegisteredVendor.MailingAddress = mailingAddress.value;
    }

    if (streetAddress && this.hasValues(streetAddress.value)) {
      this.ClientRegisteredVendor.StreetAddress = streetAddress.value;
    }
  }

  private isPrimaryContactEmailUpdated(): boolean {
    const primaryContact = this.primaryContactForm?.value as Contact;

    if (this.Company.verifyClientRegisteredVendorBusinessOnly) {
      if (this.oldPrimaryContact && primaryContact) {
        if (primaryContact.email !== this.oldPrimaryContact.email) {
          return true;
        }
      }
    }

    return false;
  }

  private checkForRequiredChanges() {
    if (!this.ClientRegisteredVendor) {
      return false;
    }

    const requiredFields = [
      'legalName',
      'ein',
      'ssn',
      'stateToVerify',
      'requesterFirstName',
      'requesterLastName'
    ];

    const requiredAddressFields = [
      'address1',
      // 'address2',
      'city',
      'state',
      'zip'
    ];

    if (this.oldClientRegisteredVendor) {
      const {
        StreetAddress: OldStreetAddress,
        MailingAddress: OldMailingAddress
      } = this.oldClientRegisteredVendor;

      const { StreetAddress, MailingAddress } = this.ClientRegisteredVendor;

      if (
        !OldStreetAddress ||
        !StreetAddress ||
        !OldMailingAddress ||
        !MailingAddress
      ) {
        return false;
      }

      const haveAddressesChanged = requiredAddressFields.every(
        (requiredField) => {
          const OldStreetAddressValue =
            OldStreetAddress[requiredField as keyof Address];
          const StreetAddressValue =
            StreetAddress[requiredField as keyof Address];
          const OldMailingAddressValue =
            OldMailingAddress[requiredField as keyof Address];
          const MailingAddressValue =
            MailingAddress[requiredField as keyof Address];

          if (
            OldStreetAddressValue !== StreetAddressValue ||
            OldMailingAddressValue !== MailingAddressValue
          ) {
            return false;
          }

          return true;
        }
      );

      if (!haveAddressesChanged) {
        return true;
      }
    }

    return !every(requiredFields, (key) => {
      const value =
        this.ClientRegisteredVendor?.[key as keyof ClientRegisteredVendor];
      if (value !== undefined && !isArray(value) && !isObject(value)) {
        if (
          this.oldClientRegisteredVendor &&
          this.oldClientRegisteredVendor[
            key as keyof ClientRegisteredVendor
          ] !== value
        ) {
          return false;
        }
      }
      return true;
    });
  }

  private setBusinessVerifiedRequiredFormFields() {
    const requiredBVFormFields: (keyof ClientRegisteredVendor)[] = [
      'stateToVerify',
      'requesterFirstName',
      'requesterLastName'
    ];

    requiredBVFormFields.forEach((field: keyof ClientRegisteredVendor) => {
      this.ClientRegisteredVendorForm.get(field)?.addValidators([
        Validators.required
      ]);
    });

    this.checkAddressValidationForBusinessVerification();

    this.ClientRegisteredVendorForm.setValidators([einSsnValidator]);
  }

  private confirmBusinessVerification() {
    return defer(() => {
      const modalRef = this.ModalService.open(
        ConfirmRunBusinessVerificationModalComponent,
        { centered: true, size: 'lg' }
      );
      modalRef.componentInstance.ClientRegisteredVendor =
        this.ClientRegisteredVendor;
      const { result } = modalRef;

      return from(result).pipe(
        catchError((response) => {
          return Promise.reject(response);
        }),
        concatMap((response) => {
          this.runBusinessVerification = true;
          return of(response);
        })
      );
    });
  }

  cancel() {
    this.location.back();
  }

  checkAddressValidationForBusinessVerification() {
    const MailingAddressControl =
      this.ClientRegisteredVendorForm.controls.MailingAddress;
    const StreetAddressControl =
      this.ClientRegisteredVendorForm.controls.StreetAddress;

    if (
      this.Company.allowAutomatedBusinessVerificationClientRegisteredVendors
    ) {
      this.requiredAddressFields.forEach((controlName) => {
        const StreetFormControl = StreetAddressControl.get(controlName);
        const MailingFormControl = MailingAddressControl.get(controlName);

        if (StreetFormControl) {
          StreetFormControl.addValidators([Validators.required]);
          StreetAddressControl.updateValueAndValidity();
        }

        if (MailingFormControl) {
          MailingFormControl.addValidators([Validators.required]);
          MailingAddressControl.updateValueAndValidity();
        }
      });
    }
  }

  submit() {
    try {
      const validationErrorsObj =
        this.UtilityService.generateValidationErrorsObject(
          this.ClientRegisteredVendorForm
        );

      if (validationErrorsObj) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot Add Vendor. You have missing required information.',
          { returnError: true }
        );
      }

      const { legalName, dba } = this.ClientRegisteredVendorForm.value;
      const nameLengthModalInputs = vendorNameLengthModalInputs({
        legalName,
        dba
      });
      if (nameLengthModalInputs) {
        const { componentInstance } = this.ModalService.open(
          ConfirmModalComponent,
          { centered: true }
        );
        Object.assign(componentInstance, nameLengthModalInputs);
        return;
      }

      this.setClientRegisteredVendor();

      if (!this.ClientRegisteredVendor) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot save vendor. No ClientRegisteredVendor record found.',
          { returnError: true }
        );
      }

      if (!this.CompanyComplianceTypeId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot save vendor. Must select a Compliance Program.',
          { returnError: true }
        );
      }

      const contactArray = [];

      if (this.primaryContactForm.value) {
        contactArray.push(this.primaryContactForm.value);
      }

      if (this.additionalContacts) {
        this.additionalContacts.forEach((contactForm) =>
          contactArray.push(contactForm.value)
        );
      }

      if (contactArray.length > 0) {
        this.ClientRegisteredVendor.Contacts = contactArray;
      }

      const observableArr = [];

      if (this.ClientRegisteredVendor) {
        observableArr.push(
          defer(() => {
            return this.ClientRegisteredVendorService.checkForDuplicateTaxId(
              this.ClientRegisteredVendor!,
              { checkNonVerified: true }
            ).pipe(
              concatMap((companyVendorOrApVendorRecords) => {
                if (
                  companyVendorOrApVendorRecords &&
                  companyVendorOrApVendorRecords.length > 0
                ) {
                  this.isDuplicate = true;

                  const { componentInstance, result } = this.ModalService.open(
                    ConfirmModalComponent,
                    { centered: true, size: 'lg' }
                  );
                  Object.assign(componentInstance, {
                    displayText: `Vendor with matching Tax ID has been found. This Vendor cannot be ${(this.ClientRegisteredVendor?.id && 'updated') || 'added'}.`,
                    hideCancelButton: true
                  });

                  return from(result).pipe(
                    map((confirm) => {
                      return false;
                    })
                  );
                }

                return of(true);
              })
            );
          })
        );
      }

      if (
        this.ClientRegisteredVendor &&
        this.ClientRegisteredVendor.id &&
        this.Company
          .allowAutomatedBusinessVerificationClientRegisteredVendors &&
        !isNil(this.oldClientRegisteredVendor) &&
        this.checkForRequiredChanges()
      ) {
        // Display confirm for running Business Verification.
        observableArr.push(this.confirmBusinessVerification());
      }

      if (this.ClientRegisteredVendor) {
        const isNewRecord = !this.ClientRegisteredVendor.id;
        const emailValue = this.primaryContactForm.get('email')?.value;

        // VPDW-2827: trigger the invitation email when an email address is
        // present on a new record, OR when it has been newly added/changed while
        // editing an existing record. Previously this whole block was gated on
        // `!id`, so editing a vendor to add an email showed no confirmation modal
        // and never set `sendInvitationEmail` (backend `sendEmail` flag), meaning
        // no invitation was ever sent on edit.
        const emailAddedOrChanged =
          !!emailValue &&
          (isNewRecord || emailValue !== this.oldPrimaryContact?.email);

        const canSendEmail =
          emailAddedOrChanged &&
          !this.Company.verifyClientRegisteredVendorBusinessOnly &&
          this.Company.allowClientRegisteredVendorEmails;

        if (canSendEmail) {
          const confirmSendEmails = defer(() => {
            const modalRef = this.ModalService.open(ConfirmModalComponent, {
              centered: true,
              size: 'lg'
            });
            modalRef.componentInstance.displayText =
              'Saving this vendor will send an invitation email to the vendor to register with VIVE';

            return modalRef.closed.pipe(
              concatMap((confirm) => {
                if (!confirm) {
                  return of(false);
                }
                this.sendInvitationEmail = true;

                return of(true);
              })
            );
          });

          observableArr.push(confirmSendEmails);
        }

        if (
          isNewRecord &&
          this.Company.allowAutomatedBusinessVerificationClientRegisteredVendors
        ) {
          observableArr.push(this.confirmBusinessVerification());
        }
      }

      if (
        this.Company.country === 'US' &&
        this.hasValues(
          pick(
            this.ClientRegisteredVendorForm.controls.MailingAddress.value,
            this.requiredAddressFields
          )
        ) &&
        this.hasValues(
          pick(
            this.ClientRegisteredVendorForm.controls.StreetAddress.value,
            this.requiredAddressFields
          )
        )
      ) {
        const normalizeAddressesObservable = defer(() => {
          const modalRef = this.ModalService.open(
            NormalizeAddressesModalComponent,
            { centered: true, size: 'lg' }
          );
          const mailingAddress =
            this.ClientRegisteredVendorForm.controls.MailingAddress;
          const streetAddress =
            this.ClientRegisteredVendorForm.controls.StreetAddress;
          modalRef.componentInstance.mailingAddress = mailingAddress?.value;
          modalRef.componentInstance.streetAddress = streetAddress?.value;

          return modalRef.closed.pipe(
            concatMap((result) => {
              if (!result) {
                return of(false);
              }
              mailingAddress?.patchValue(result.mailingAddress);
              streetAddress?.patchValue(result.streetAddress);

              return of(true);
            })
          );
        });

        observableArr.push(normalizeAddressesObservable);
      }

      if (
        !this.primaryContactForm.get('email')?.value &&
        !this.Company.verifyClientRegisteredVendorBusinessOnly
      ) {
        const invitationEmailConfirmObservable = defer(() => {
          const modalRef = this.ModalService.open(ConfirmModalComponent, {
            centered: true
          });
          modalRef.componentInstance.displayText =
            'An invitation email cannot be sent to this vendor without entering a Contact email. Do you want to continue?';
          return modalRef.closed;
        });

        observableArr.push(invitationEmailConfirmObservable);
      }

      if (this.isPrimaryContactEmailUpdated()) {
        const primaryContactUpdateConfirmObservable = defer(() => {
          const modalRef = this.ModalService.open(ConfirmModalComponent, {
            centered: true
          });
          modalRef.componentInstance.displayText =
            "Primary Contact Email has been updated. If you want to send an invitation email to the vendor, please click the Vendor Registration Code link in the Vendor's Overview tab. Do you want to continue?";

          return modalRef.closed;
        });

        observableArr.push(primaryContactUpdateConfirmObservable);
      }

      if (
        this.attemptToConvertToFullProfile &&
        this.ClientRegisteredVendor &&
        this.Vendor &&
        this.Vendor.id
      ) {
        observableArr.push(
          defer(() => {
            return this.ClientRegisteredVendorService.attemptToConvertViveVendorToClientRegisteredVendor(
              this.ClientRegisteredVendor!,
              {
                VendorId: this.Vendor?.id,
                CompanyId: this.CompanyId
              }
            ).pipe(
              concatMap((convertedClientRegisteredVendor) => {
                if (!convertedClientRegisteredVendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Something went wrong trying to convert vendor.',
                    { returnError: true }
                  );
                }

                this.ClientRegisteredVendor = convertedClientRegisteredVendor;

                return of(true);
              })
            );
          })
        );
      }

      if (this.ClientRegisteredVendor) {
        // VPDW-763: before creating the vendor, let the user designate which
        // accounting system(s) it should be created in. Only prompts when the
        // client has 2+ create-eligible systems; cancelling aborts the save.
        observableArr.push(
          defer(() =>
            this.AccountingSystemSelectionService.selectApConfigIds(
              this.CompanyId
            ).pipe(
              concatMap((apConfigIds) => {
                if (apConfigIds === null) {
                  return of(false);
                }

                this.selectedApConfigIds = apConfigIds ?? undefined;

                return of(true);
              })
            )
          )
        );

        observableArr.push(
          defer(() => {
            const { nativeElement } = this.HostElement;
            const mask = new LoadingMask(nativeElement, {
              maskText: 'Saving Vendor...'
            });

            return this.ClientRegisteredVendorService.saveClientRegisteredVendorHandleCompliance(
              this.ClientRegisteredVendor!,
              this.CompanyComplianceTypeId,
              {
                runBusinessVerification: this.runBusinessVerification,
                sendEmail: this.sendInvitationEmail,
                sendCreationEmail: true,
                createApVendor: !this.isDuplicate,
                ...(this.selectedApConfigIds && this.selectedApConfigIds.length
                  ? { apConfigIds: this.selectedApConfigIds }
                  : {})
              }
            ).pipe(
              concatMap((ClientRegisteredVendor: ClientRegisteredVendor) => {
                mask.destroy();
                if (ClientRegisteredVendor) {
                  this.ClientRegisteredVendor = ClientRegisteredVendor;
                  this.loadClientRegisteredVendor();
                  if (this.ClientRegisteredVendor) {
                    this.ToastService.addSuccessToast(
                      'Success',
                      'Successfully saved ClientRegisteredVendor'
                    );
                  }

                  return of(true);
                }

                return of(false);
              }),
              catchError(() => {
                mask.destroy();

                return of(false);
              })
            );
          })
        );
      }

      concat(...observableArr)
        .pipe(takeWhile((result) => result !== false))
        .subscribe({
          complete: () => {
            if (this.ClientRegisteredVendor) {
              const { id: ClientRegisteredVendorId } =
                this.ClientRegisteredVendor;
              const { UserAccountType } = this.LoggedInUser;
              const { name } = UserAccountType!;

              if (name === 'Client' && ClientRegisteredVendorId) {
                this.Router.navigate([
                  'client',
                  this.CompanyId,
                  'vendors',
                  'my-vendor',
                  'client-registered-vendor',
                  ClientRegisteredVendorId,
                  'info'
                ]);
              }
            }
          }
        });
    } catch (error) {
      this.disabled = false;
    }
  }

  loadCompanyData() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot load company data. No Company ID found.',
        { returnError: true }
      );
    }

    from(
      companiesService.findOneById({
        pathParams: {
          companyId: this.CompanyId
        },
        queryParams: {
          includeAssociations: [
            {
              name: 'CompanyComplianceTypes'
            }
          ]
        }
      })
    ).subscribe({
      next: (Company: CompanyDto) => {
        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot Add Vendor. No Company Record Found',
            { returnError: true }
          );
        }

        this.Company = Company;

        const {
          allowAutomatedBusinessVerificationClientRegisteredVendors,
          allowClientRegisteredVendors,
          CompanyComplianceTypes,
          country
        } = Company;

        if (
          this.addingClientRegisteredVendor &&
          !allowClientRegisteredVendors
        ) {
          this.ToastService.addErrorToast(
            'Error',
            'This client is not configured to add Business Verified or Non-Verified vendors.',
            { returnError: false }
          );
          this.Router.navigate(['client', this.CompanyId, 'vendors', 'client']);
          return;
        }

        if (allowAutomatedBusinessVerificationClientRegisteredVendors) {
          this.setBusinessVerifiedRequiredFormFields();
          this.isBusinessVerifiedVendor = true;
        }

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Client has no country.',
            { returnError: true }
          );
        }

        const {
          controls: { MailingAddress, StreetAddress }
        } = this.ClientRegisteredVendorForm;

        MailingAddress.patchValue({ country });
        StreetAddress.patchValue({ country });

        this.zipMask =
          country === 'US'
            ? this.UtilityService.usZipMask
            : this.UtilityService.canadianZipMask;

        this.LocalizationInfo =
          this.UtilityService.getLocalizedValuesMap(country);
        this.SubNationalDivisionService.getSubNationalDivisions({
          country: country === 'US' ? 'USA' : 'Canada'
        }).subscribe({
          next: (data: SubNationalDivision[]) => {
            this.states = data;
            this.ChangeDetectorRef.markForCheck();
          }
        });

        if (!CompanyComplianceTypes || CompanyComplianceTypes.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot Add Vendor. No Company Compliance Types Found.',
            { returnError: true }
          );
        }

        this.CompanyComplianceTypes = CompanyComplianceTypes;
        const DefaultComplianceType = CompanyComplianceTypes.find(
          (cct) => cct.type === 'default'
        );

        if (DefaultComplianceType && !this.CompanyComplianceTypeId) {
          this.CompanyComplianceTypeId = DefaultComplianceType.id;
        }
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  setupPrimaryAndAdditionalContacts(Contacts?: Contact[]) {
    if (Contacts && Contacts.length > 0) {
      const primaryContacts = Contacts.filter(
        (contact) => contact.isPrimaryCompliance
      );
      const additionalContacts = Contacts.filter((contact) => {
        const { isBid, isBilling, isCompliance, isPrimaryCompliance } = contact;
        return !isBid && !isBilling && !isCompliance && !isPrimaryCompliance;
      });

      const PrimaryContact = primaryContacts[0];

      if (PrimaryContact) {
        // VPDW-2827: snapshot the existing primary contact so the edit flow can
        // detect when an email address is newly added/changed on save.
        this.oldPrimaryContact = structuredClone(PrimaryContact);

        const { isCompliance } = PrimaryContact;
        const otherComplianceContacts = Contacts.filter(
          (c) => c.isCompliance && !c.isPrimaryCompliance
        );

        if (!isCompliance && otherComplianceContacts.length > 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot setup primary contact. Primary compliance contact is not a compliance contact and other compliance contacts exist.',
            { returnError: true }
          );
        }

        if (!isCompliance) {
          Object.assign(PrimaryContact, { isCompliance: true });
        }

        this.primaryContactForm.patchValue(PrimaryContact);

        this.isOffsiteChanged();
      }

      if (additionalContacts && additionalContacts.length > 0) {
        additionalContacts.forEach((contact) => {
          const newContactForm = new ContactForm();
          newContactForm.patchValue(contact);
          this.removeStreetAndMailingAddress(newContactForm);

          this.additionalContacts.push(newContactForm);
        });
      }
    }
  }

  removeStreetAndMailingAddress(form: ContactForm) {
    const StreetAddressForm = form.controls.StreetAddress;
    const MailingAddressForm = form.controls.MailingAddress;

    if (StreetAddressForm) {
      form.removeControl('StreetAddress');
    }

    if (MailingAddressForm) {
      form.removeControl('MailingAddress');
    }

    form.updateValueAndValidity;
  }

  loadClientRegisteredVendor() {
    this.editingClientRegisteredVendor = true;
    this.oldClientRegisteredVendor = structuredClone(
      this.ClientRegisteredVendor
    );

    if (!this.oldClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Something went wrong trying to copy existing ClientRegisteredVendor.',
        { returnError: true }
      );
    }

    const {
      Contacts,
      CompanyVendors,
      StreetAddress,
      MailingAddress,
      Addresses
    } = this.oldClientRegisteredVendor;

    this.ClientRegisteredVendorForm.patchValue(this.oldClientRegisteredVendor);

    this.setupPrimaryAndAdditionalContacts(Contacts);

    if (CompanyVendors && CompanyVendors.length > 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'ClientRegisteredVendor should not have multiple clients.',
        { returnError: true }
      );
    }

    if (CompanyVendors) {
      const companyVendor = CompanyVendors![0];
      const { CompanyComplianceTypeId, CompanyId } = companyVendor;

      if (CompanyId) {
        this.CompanyId = CompanyId;

        this.loadCompanyData();
      }

      if (CompanyComplianceTypeId) {
        this.CompanyComplianceTypeId = CompanyComplianceTypeId;
      }
    }

    const fieldsToCheck: (keyof Address)[] = [
      'address1',
      'address2',
      'city',
      'state',
      'zip'
    ];

    if (StreetAddress && MailingAddress) {
      const isStreetAndMailingSame = fieldsToCheck.every(
        (field: keyof Address) => {
          if (field !== null) {
            return StreetAddress[field] === MailingAddress[field];
          }

          return false;
        }
      );

      if (isStreetAndMailingSame) {
        this.onIsMailingAndStreetSameChange();
      }
    }

    this.isTakeOverChanged();
  }

  checkForRequiredValidators(formControl: FormControl) {
    return formControl.hasValidator(Validators.required);
  }

  ngOnInit(): void {
    const paramMap = this.route.snapshot.paramMap;
    const ClientRegisteredVendorId = Number(
      paramMap.get('ClientRegisteredVendorId')
    );

    if (this.attemptToConvertToFullProfile && this.Vendor) {
      const { Contacts } = this.Vendor;

      this.ClientRegisteredVendorForm.patchValue(this.Vendor);
      this.setupPrimaryAndAdditionalContacts(Contacts);
    } else if (this.ClientRegisteredVendor) {
      this.loadClientRegisteredVendor();
    } else if (ClientRegisteredVendorId) {
      this.ClientRegisteredVendorService.getClientRegisteredVendor(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: ClientRegisteredVendorId,
          isArchived: { $in: [true, false] },
          StreetAddress: {},
          MailingAddress: {},
          Contact: {},
          CompanyVendor: {
            attributes: ['id', 'CompanyComplianceTypeId', 'CompanyId'],
            Company: {}
          }
        })
      ).subscribe({
        next: (ClientRegisteredVendor: ClientRegisteredVendor) => {
          this.ClientRegisteredVendor = ClientRegisteredVendor;
          this.loadClientRegisteredVendor();
          this.ChangeDetectorRef.markForCheck();
        }
      });
    } else {
      this.addingClientRegisteredVendor = true;
      this.loadCompanyData();
    }
  }
}
